/**
 * Upload a file straight to object storage through a presigned URL, reporting progress.
 *
 * `fetch` cannot report upload progress, and on a phone over mobile data a photo can take long
 * enough that a bar is the difference between waiting and giving up. So this is the one place
 * the app uses `XMLHttpRequest`.
 *
 * The request carries exactly one header, the `Content-Type` the URL was signed for. Storage
 * refuses the upload if it differs, which is the point: the client declared a JPEG when it asked
 * for the URL and cannot then send something else. No cookies and no CSRF token go with it --
 * this request leaves our origin, and the signature in the URL is its only credential.
 */

export class UploadError extends Error {
  readonly status: number;

  constructor(status: number) {
    // Status 0 is what a browser reports for a network failure or a CORS refusal: the request
    // never produced a response the page is allowed to see.
    super(status === 0 ? "The upload could not reach storage" : `Storage answered ${status}`);
    this.name = "UploadError";
    this.status = status;
  }
}

/** The slice of `XMLHttpRequest` this module uses, so tests can supply their own. */
export interface UploadRequest {
  open(method: string, url: string): void;
  setRequestHeader(name: string, value: string): void;
  send(body: Blob): void;
  readonly status: number;
  upload: { onprogress: ((event: ProgressEvent) => void) | null };
  onload: ((event: ProgressEvent) => void) | null;
  onerror: ((event: ProgressEvent) => void) | null;
}

export function putWithProgress(
  url: string,
  file: Blob,
  contentType: string,
  onProgress: (fraction: number) => void,
  makeRequest: () => UploadRequest = () => new XMLHttpRequest(),
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = makeRequest();
    request.open("PUT", url);
    request.setRequestHeader("Content-Type", contentType);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress(1);
        resolve();
      } else {
        reject(new UploadError(request.status));
      }
    };
    request.onerror = () => reject(new UploadError(0));
    request.send(file);
  });
}

/**
 * The content type to declare for a picked file.
 *
 * Browsers usually know, but some report an empty type for HEIC -- the format an iPhone uses by
 * default -- so the extension is the fallback. An unknown type is passed through as it is and
 * the API refuses it with a message, which is clearer than guessing here.
 */
export function contentTypeOf(file: { name: string; type: string }): string {
  if (file.type) return file.type;
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  const byExtension: Record<string, string> = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    heic: "image/heic",
    heif: "image/heif",
  };
  return byExtension[extension] ?? "application/octet-stream";
}
